import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SectionTabs from '../components/SectionTabs';
import { CalendarDays, FilePlus, Mail, Package } from 'lucide-react';
import LottieAnim from '../components/LottieAnim';

function BigDataUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);

  const fileValueChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleDownload = () => {
    if (!dataType) {
      alert('데이터 타입을 선택해 주세요.');
      return;
    }
    let href = '';
    let downloadName = '';
    if (dataType === '권리사') {
      href = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/BigDataClient.xlsx';
      downloadName = 'BigDataClient.xlsx';
    } else if (dataType === '아티스트') {
      href = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/BigDataArtist.xlsx';
      downloadName = 'BigDataArtist.xlsx';
    } else if (dataType === '앨범') {
      href = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/BigDataAlbum.xlsx';
      downloadName = 'BigDataAlbum.xlsx';
    } else if (dataType === '음반') {
      href = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/BigDataPhysicalAlbum.xlsx';
      downloadName = 'BigDataPhysicalAlbum.xlsx';
    }
    const link = document.createElement('a');
    link.href = href;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pollProgress = (type) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/bigDataUpload/progress?type=${type}`;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(endpoint);
        setProgress(res.data);
        if (res.data.progress >= 100) {
          clearInterval(id);
        }
      } catch (err) {
        clearInterval(id);
      }
    }, 1000);
  };

  const doUpload = async ({ overwrite = false } = {}) => {
    setLoading(true);
    setProgress({ progress: 0, total: 0 });
    setUploadErrors([]);

    let url = '';
    if (dataType === '권리사') url = `${process.env.REACT_APP_API_URL}/bigDataUpload/client/`;
    else if (dataType === '아티스트') url = `${process.env.REACT_APP_API_URL}/bigDataUpload/artist/`;
    else if (dataType === '앨범') url = `${process.env.REACT_APP_API_URL}/bigDataUpload/album/`;
    else if (dataType === '음반') url = `${process.env.REACT_APP_API_URL}/bigDataUpload/physicalAlbum/`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('update', dataType === '음반' ? 'true' : 'false');
    if (overwrite) formData.append('overwrite', 'true');

    pollProgress(dataType);

    try {
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data;

      // 유효성 오류
      if (data.errors && data.errors.length > 0) {
        setUploadErrors(data.errors);
        return;
      }

      // 애매한 중복 앨범 감지 (앨범명·발매일은 같은데 일부 정보가 다름) → 덮어쓰기 확인
      if (data.conflicts && data.conflicts.length > 0) {
        const list = data.conflicts
          .map((c) => {
            let line = `• ${c.nameKr} (${c.releaseDate})`;
            if (c.artistDiff) {
              line += `\n   ↳ 아티스트 다름 — 기존: ${c.existingArtistName} / 엑셀: ${c.excelArtistName}`;
            }
            return line;
          })
          .join('\n');
        const ok = window.confirm(
          `같은 앨범명·발매일의 앨범이 이미 등록되어 있지만 일부 정보가 달라 확인이 필요합니다:\n\n${list}\n\n같은 앨범이 맞다면 [확인]을 눌러주세요. 기존 앨범에 엑셀 내용을 덮어써서 업데이트합니다.\n(취소 시 업로드가 중단됩니다.)`
        );
        if (ok) {
          await doUpload({ overwrite: true });
        }
        return;
      }

      const skipped = data.skipped || [];
      const registered = dataType === '음반' ? (data.saved ?? 0) : (data.data?.length ?? 0);
      let msg = `대량 등록을 완료하였습니다. (${registered}건 등록)`;
      if (skipped.length > 0) msg += `\n중복으로 스킵된 항목 ${skipped.length}건: ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? ' 외...' : ''}`;
      alert(msg);

      if (dataType === '권리사') navigate('/Clients');
      else if (dataType === '아티스트') navigate('/Artists');
      else if (dataType === '앨범') navigate('/Albums');
      else navigate('/PhysicalAlbums');
    } catch (error) {
      const msg =
        error.response?.data?.errorMessage ||
        error.response?.data?.error ||
        error.response?.data?.message;
      alert(msg ? `오류: ${msg}` : '등록 중 알 수 없는 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const add = async () => {
    if (!dataType) { alert('데이터 타입을 선택해 주세요.'); return; }
    if (!file) { alert('파일을 선택해 주세요.'); return; }
    await doUpload();
  };

  const isStructuredErrors = uploadErrors.length > 0 && typeof uploadErrors[0] === 'object';

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">대량등록 엑셀 업로드</div>
      </div>

      <SectionTabs items={[
        { label: '음원 월정산', to: '/MonthlyUpload', icon: CalendarDays },
        { label: '음반 월정산', to: '/PhysicalUpload', icon: Package },
        { label: '대량등록', to: '/BigDataUpload', icon: FilePlus },
        { label: '권리사메일발송', to: '/EmailToClients', icon: Mail },
      ]} />

      {/* 업로드 */}
      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#182334] p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-300">엑셀 업로드</h3>

        {/* 데이터 타입 선택 */}
        <div className="flex gap-2">
          {['권리사', '아티스트', '앨범', '음반'].map((type) => (
            <button
              key={type}
              onClick={() => setDataType(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                dataType === type
                  ? 'bg-sky-500 border-sky-500 text-white'
                  : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'
              }`}>
              {type}
            </button>
          ))}
        </div>

        <input
          type="file"
          accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={fileValueChange}
          className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-500/10 file:text-sky-400 hover:file:bg-sky-500/20 hover:cursor-pointer"
        />

        <div className="flex items-center gap-3">
          <button
            onClick={add}
            disabled={loading || !file || !dataType}
            className="px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 text-sm font-semibold rounded-xl disabled:opacity-50 transition-colors">
            {loading ? '처리 중...' : '업로드'}
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-300 text-sm font-semibold transition-colors"
            onClick={handleDownload}>
            양식 다운로드
          </button>
        </div>
      </div>

      {/* 유효성 오류 목록 */}
      {uploadErrors.length > 0 && (
        <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-rose-400 text-lg">⚠️</span>
            <span className="text-sm font-semibold text-rose-400">
              {uploadErrors.length}개 오류 발견 — 전체 등록이 취소되었습니다. 아래 내용을 수정 후 다시 업로드하세요.
            </span>
          </div>

          {isStructuredErrors ? (
            <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-xl border border-rose-500/20">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-rose-900/60">
                  <tr>
                    <th className="px-3 py-2 text-left text-rose-300 font-semibold whitespace-nowrap">행</th>
                    <th className="px-3 py-2 text-left text-rose-300 font-semibold whitespace-nowrap">항목</th>
                    <th className="px-3 py-2 text-left text-rose-300 font-semibold whitespace-nowrap">입력값</th>
                    <th className="px-3 py-2 text-left text-rose-300 font-semibold">오류 내용</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadErrors.map((err, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-rose-500/5' : 'bg-transparent'}>
                      <td className="px-3 py-2 text-rose-200 font-mono whitespace-nowrap">{err.row}행</td>
                      <td className="px-3 py-2 text-amber-300 font-semibold whitespace-nowrap">{err.field}</td>
                      <td className="px-3 py-2 text-slate-300 font-mono max-w-32 truncate" title={err.value}>
                        {err.value || <span className="text-slate-500 italic">비어있음</span>}
                      </td>
                      <td className="px-3 py-2 text-rose-300">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {uploadErrors.map((err, i) => (
                <div key={i} className="text-xs text-rose-300 font-mono">{err}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && progress && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="text-center">
            <LottieAnim type="loading" size={80} />
            <div className="w-80 mx-auto bg-white/10 rounded-full h-4 mb-4 mt-2">
              <div
                className="h-4 rounded-full transition-all duration-300"
                style={{
                  width: `${progress.progress}%`,
                  background: 'linear-gradient(to right, #4338ca, #1d4ed8)',
                }}
              />
            </div>
            <div className="text-white text-lg font-semibold">
              등록중... {progress.progress}%
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BigDataUploadPage;
